import {loadIntegrationList} from "./integrationRequestLifecycle.js";
import {useTranslation} from "react-i18next";
import useDownloadState from "../../../api/utilities/useDownloadState.js";
import {useContext, useEffect, useMemo, useState} from "react";
import {ProjectIntegrationsApi} from "../../../api/authority/generated/index";
import FormError from "../../../components/form/FormError.jsx";
import {GlobalContext} from "../../../utils/context/GlobalContextProvider.jsx";
import {faRefresh} from "@fortawesome/free-solid-svg-icons/faRefresh";
import {faArrowLeft, faPlus} from "@fortawesome/free-solid-svg-icons";
import ThirdPartyServiceRow from "./ThirdPartyServiceRow.jsx";
import {useNavigate, useSearchParams} from "react-router-dom";
import NarrowSecureLayout from "../../../layout/NarrowSecureLayout.jsx";
import BrandLogo from "../../../utils/brands/BrandLogo.jsx";
import ThirdPartyServiceEditForm from "./ThirdPartyServiceEditForm.jsx";
import Button from "../../../components/Button.jsx";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import LoadingComponent from "../../../components/LoadingComponent.jsx";
import {useBrand, useBrands} from "../../../utils/brands/branding.js";
import SupportLink from "../../../components/SupportLink.jsx";
import ActionButton from "../../../components/ActionButton.jsx";
import {useIsAdministrator} from "../../../components/auth/rolesAndPermissions.js";
import {isBrandVisibleToUser} from "../../../utils/brands/brandVisibility.js";
import {getIntegrationState} from "./integrationListPresentation.js";
import {getConfiguredIntegrationGroups, getConfiguredIntegrationName, getIntegrationDisplayName} from "./integrationPickerPresentation.js";

function integrationGroupTitle(key, t) {
    switch (key) {
        case 'shop-and-leads': return t('project.integration-category.shop-and-leads', {defaultValue: 'Zdroje dat'});
        case 'marketing': return t('project.integration-category.marketing', {defaultValue: 'Marketingové integrace'});
        case 'data-and-analytics': return t('project.integration-category.data-and-analytics', {defaultValue: 'Datové a analytické integrace'});
        default: return t('project.integration-category.other', {defaultValue: 'Ostatní integrace'});
    }
}

export default function ThirdPartyServiceIntegrations({project}) {

    let {t} = useTranslation();
    let navigate = useNavigate();
    let isAdministrator = useIsAdministrator();
    let brands = useBrands();

    let [state, setDownloading, setResult, setError] = useDownloadState();
    let [refresh, setRefresh] = useState(0);

    let {getAuthorityConfiguration} = useContext(GlobalContext);

    let [searchParam, setSearchParams] = useSearchParams();

    useEffect(() => {
        let active = true;

        setDownloading(true);

        loadIntegrationList(
            () => new ProjectIntegrationsApi(getAuthorityConfiguration()).listProjectIntegrations({projectId: project.id}),
            {isActive: () => active, onResult: setResult, onError: setError, onSettled: () => setDownloading(false)}
        );

        return () => { active = false; };

    }, [project.id, refresh]);

    let selectedProjectIntegration = useMemo(() => {
        let editIntegrationId = searchParam.get('integrationId');
        for (let integration of state.result ?? []) {
            // noinspection JSUnresolvedReference
            if (
                integration.id === editIntegrationId
                && isBrandVisibleToUser(brands[integration.name], isAdministrator, true)
            ) {
                return integration;
            }
        }

        return null;

    }, [brands, isAdministrator, project.id, refresh, searchParam, searchParam.get('integrationId'), state.result]);

    let visibleProjectIntegrations = useMemo(() => {
        return (state.result ?? []).filter((integration) => {
            return isBrandVisibleToUser(brands[integration.name], isAdministrator, true);
        });
    }, [brands, isAdministrator, state.result]);

    let visibleIntegrationGroups = useMemo(
        () => getConfiguredIntegrationGroups(visibleProjectIntegrations, brands),
        [brands, visibleProjectIntegrations]
    );

    let microsoftOAuthAlert = useMemo(() => {
        const errorMessage = searchParam.get('microsoftOAuthErrorMessage');

        if (!errorMessage) {
            return null;
        }

        const errorCode = searchParam.get('microsoftOAuthErrorCode');

        return {
            id: 'microsoft-oauth-error',
            type: 'danger',
            showClose: false,
            content: errorCode
                ? `${errorMessage} (${errorCode})`
                : errorMessage,
        };
    }, [searchParam]);

    let metaOAuthAlert = useMemo(() => {
        const errorMessage = searchParam.get('metaOAuthErrorMessage');

        if (!errorMessage) {
            return null;
        }

        const errorCode = searchParam.get('metaOAuthErrorCode');

        return {
            id: 'meta-oauth-error',
            type: 'danger',
            showClose: false,
            content: errorCode
                ? `${errorMessage} (${errorCode})`
                : errorMessage,
        };
    }, [searchParam]);

    // noinspection JSUnresolvedReference
    let brand = useBrand(selectedProjectIntegration?.name);
    const selectedIntegrationState = selectedProjectIntegration
        ? getIntegrationState(selectedProjectIntegration)
        : 'inactive';

    if (selectedProjectIntegration !== null && brand !== null) {
        //let brand = Branding[selectedProjectIntegration.name];

        return <NarrowSecureLayout
            title={<div className={'inline-flex flex-wrap items-center gap-3'}>
                <BrandLogo brand={brand} className={'mx-aut my-auto'}/>
                <div className={'mx-aut my-auto'}>{getConfiguredIntegrationName(selectedProjectIntegration?.name, brand)}</div>
                <span
                    className={
                        'inline-flex items-center rounded border px-3 py-1 text-sm font-medium ' +
                        (selectedIntegrationState === 'active'
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            : selectedIntegrationState === 'paused'
                                ? 'border-amber-200 bg-amber-50 text-amber-800'
                                : 'border-rose-200 bg-rose-50 text-datixo-negative')
                    }
                >
                    {selectedIntegrationState === 'active'
                        ? t('project.integration-state-active', {defaultValue: 'Aktivní'})
                        : selectedIntegrationState === 'paused'
                            ? t('project.integration-state-paused', {defaultValue: 'Pozastaveno'})
                            : t('project.integration-state-inactive', {defaultValue: 'Neaktivní'})}
                </span>
            </div>
            }
            actions={<>
                <Button
                    variant={'outline-primary'}
                    onClick={() => navigate('/project/' + project.id + '/settings/integrations')}
                >
                    <span className={'inline-flex items-center gap-2'}>
                        <FontAwesomeIcon icon={faArrowLeft}/>
                        <span>{t('project.back-to-integrations', {defaultValue: 'Zpět'})}</span>
                    </span>
                </Button>
                {brand.helpArticleId &&
                    <SupportLink
                        articleId={brand.helpArticleId}
                        title={t('support.help-for-integration', {integrationName: brand.name})
                    }/>
                }
            </>}
        >
            <ThirdPartyServiceEditForm
                project={project}
                brand={brand}
                projectIntegration={selectedProjectIntegration}
                onIntegrationChanged={(updatedIntegration) => setResult((currentIntegrations) =>
                    (currentIntegrations ?? []).map((integration) =>
                        integration.id === updatedIntegration.id ? updatedIntegration : integration
                    )
                )}
            />
        </NarrowSecureLayout>;
    }

    return (
        <NarrowSecureLayout
            title={t('project.3rd-party-integrations')}
            compactHeaderSpacing
            actions={<>
                <div className="flex gap-1 justify-center items-center">
                    <ActionButton icon={'plus'} onClick={() => navigate('/project/' + project.id + '/settings/integrations/create')} />
                    <ActionButton icon={'sync'} onClick={() => setRefresh((refresh) => refresh + 1)} />
                </div>
            </>}
            alerts={[
                microsoftOAuthAlert,
                metaOAuthAlert,
                {
                    id: 'utm-pdf-settings',
                    type: 'primary',
                    showClose: false,
                    className: '',
                    content: (
                        <span>
                            Pro párování objednávek s reklamními kampaněmi je klíčové správné{' '}
                            <a
                                href="https://datixo.com/documents/UTM_Parameters_Setup.pdf"
                                target="_blank"
                                rel="noreferrer"
                                className="font-medium text-violet-700 underline decoration-violet-300 underline-offset-2 hover:text-violet-800"
                            >
                                nastavení UTM parametrů
                            </a>
                            .
                        </span>
                    ),
                },
            ]}
        >
            <LoadingComponent loading={state.downloading}>
                {state.error && <FormError error={state.error}/>}
                {state.result && visibleIntegrationGroups.length === 0 &&
                    <div className={'text-xs text-center italic'}>{t('project.no-integrations-found')}</div>
                }
                {visibleIntegrationGroups.map(({key, integrations}) => <section key={key} className={'mb-5 last:mb-0'}>
                    <h2 className={'mb-2 text-sm font-semibold text-slate-500 dark:text-slate-400'}>
                        {integrationGroupTitle(key, t)}
                    </h2>
                    {integrations.map((projectIntegration) => <ThirdPartyServiceRow projectIntegration={projectIntegration} key={projectIntegration.id}/>)}
                </section>)}
            </LoadingComponent>
        </NarrowSecureLayout>
    );
}
